import React from 'react';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';
import {
  ArrowRight, Calendar, FileText, Shield, MessageSquare, CheckCircle2,
  Mail, Clock, Building2, PenLine, ShieldCheck, Stamp, Download,
} from 'lucide-react';

// External booking link
const CALENDLY_URL = 'https://calendly.com/santoshdealdocs/strategy-consultation';

// ── Palette per spec ────────────────────────────────────────────────────────
//   Background: #0b1310   Accent / CTA: #059669
const BG = 'bg-[#0b1310]';
const BG_PANEL = 'bg-[#111827]';
const BORDER = 'border-[#1f2937]';
const ACCENT = '#059669';

// ── Reusable subcomponents ──────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <span className="text-[#059669] font-semibold text-xs uppercase tracking-[0.18em]">
    {children}
  </span>
);

const ReasonCard = ({ title, body, testId }) => (
  <div
    className={`${BG_PANEL} ${BORDER} border rounded-xl p-7 hover:border-[#059669]/40 transition-colors`}
    data-testid={testId}
  >
    <h3 className="text-lg font-semibold text-white mb-3">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
  </div>
);

const DocBullet = ({ children }) => (
  <li className="flex items-start gap-3 py-1.5">
    <span
      className="w-1.5 h-1.5 rounded-full bg-[#059669] shrink-0 mt-2"
      aria-hidden="true"
    />
    <span className="text-sm text-slate-300 leading-relaxed">{children}</span>
  </li>
);

const Step = ({ n, title, body, testId }) => (
  <div className="flex gap-5" data-testid={testId}>
    <div
      className="w-10 h-10 rounded-full bg-[#059669] text-white flex items-center justify-center text-base font-bold shrink-0"
      aria-hidden="true"
    >
      {n}
    </div>
    <div>
      <h4 className="text-lg font-semibold text-white mb-1.5">{title}</h4>
      <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  </div>
);

const PrimaryCTA = ({ testId, children }) => (
  <a
    href={CALENDLY_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 bg-[#059669] text-white font-semibold text-sm md:text-base px-6 py-3.5 rounded-lg hover:bg-[#047857] transition-colors"
    data-testid={testId}
  >
    {children} <ArrowRight className="w-4 h-4" />
  </a>
);

// ── Step block (text + mockup, alternating layout) ──────────────────────────
const StepBlock = ({ number, title, body, mockup, mockupOnRightDesktop, testId }) => {
  // Approach A: text first in source → always above mockup on mobile.
  // On desktop, swap order so even steps put mockup on the left.
  const textOrder = mockupOnRightDesktop ? 'md:order-1' : 'md:order-2';
  const mockupOrder = mockupOnRightDesktop ? 'md:order-2' : 'md:order-1';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center" data-testid={testId}>
      <div className={`${textOrder}`}>
        <p className="text-6xl md:text-7xl font-bold text-[#059669]/20 tracking-tighter leading-none mb-4">
          {number}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight mb-4">
          {title}
        </h3>
        <p className="text-base md:text-lg text-slate-400 leading-relaxed">{body}</p>
      </div>
      <div className={`${mockupOrder}`}>{mockup}</div>
    </div>
  );
};

const StatusBadge = ({ tone, children }) => {
  const map = {
    draft:    'bg-amber-500/15 text-amber-300 border-amber-500/30',
    pending:  'bg-slate-500/15 text-slate-300 border-slate-500/30',
    approved: 'bg-[#059669]/15 text-[#059669] border-[#059669]/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${map[tone]}`}>
      {children}
    </span>
  );
};

// ─── Mockup 1: Document list (Step 01) ──────────────────────────────────────
const DocumentListMockup = () => {
  const docs = [
    { title: 'SAFE Agreement — Seed Round', sub: 'Post-Money SAFE for $500,000 investment', v: 'v2' },
    { title: 'Founder Agreement — Acme AI Inc.', sub: 'Equity vesting and IP assignment agreement', v: 'v1' },
    { title: 'Board Resolution — SAFE Issuance', sub: 'Unanimous written consent of the Board', v: 'v1' },
  ];
  return (
    <div className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl overflow-hidden" data-testid="mockup-document-list">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[#1f2937] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#059669]/15 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-[#059669]" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm leading-none">Acme AI Inc.</p>
            <span className="text-[10px] text-slate-500">C-Corporation · Delaware</span>
          </div>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#059669] bg-[#059669]/10 border border-[#059669]/30 px-2 py-1 rounded">C-Corp</span>
      </div>
      {/* Tabs */}
      <div className="px-5 pt-3 flex items-center gap-5 border-b border-[#1f2937]">
        <span className="text-sm font-semibold text-white pb-2 border-b-2 border-[#059669]">Documents</span>
        <span className="text-sm text-slate-500 pb-2">Activity Log</span>
      </div>
      {/* Rows */}
      <ul className="divide-y divide-[#1f2937]">
        {docs.map((d, i) => (
          <li key={i} className="px-5 py-3.5 hover:bg-white/[0.02]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{d.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{d.sub}</p>
              </div>
              <span className="text-[10px] font-mono text-slate-400 shrink-0">{d.v}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <StatusBadge tone="draft">draft</StatusBadge>
              <span className="text-[10px] text-slate-500">Jun 20, 2026</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Mockup 2: Email invite + OTP login (Step 02) ───────────────────────────
const EmailInviteAndOtpMockup = () => (
  <div className="space-y-5">
    {/* Email card */}
    <div className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl overflow-hidden" data-testid="mockup-email-invite">
      <div className="px-5 py-3 border-b border-[#1f2937] flex items-center gap-2 text-slate-500 text-xs">
        <Mail className="w-3.5 h-3.5" />
        <span>From: notifications@starteaseai.com</span>
      </div>
      <div className="px-5 py-5">
        <p className="text-base font-semibold text-white mb-3">You&apos;ve been invited to review a document</p>
        <p className="text-xs text-slate-400 mb-1"><span className="text-slate-500">Company:</span> Acme AI Inc.</p>
        <p className="text-xs text-slate-400 mb-4"><span className="text-slate-500">Document:</span> SAFE Agreement — Seed Round</p>
        <p className="text-sm text-slate-300 leading-relaxed mb-4">
          Click below to access the document. You&apos;ll receive a 6-digit OTP to log in — no account or password needed.
        </p>
        <a
          href={CALENDLY_URL}
          onClick={(e) => e.preventDefault()}
          className="inline-flex items-center gap-2 bg-[#059669] text-white font-semibold text-sm px-4 py-2.5 rounded-lg"
        >
          Review Document <ArrowRight className="w-3.5 h-3.5" />
        </a>
        <p className="text-[10px] text-slate-600 mt-5">Powered by StartEase Agent</p>
      </div>
    </div>

    {/* OTP login */}
    <div className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl px-5 py-6" data-testid="mockup-otp-login">
      <h4 className="text-base font-semibold text-white mb-1">Enter your access code</h4>
      <p className="text-xs text-slate-500 mb-4">We sent a 6-digit code to investor@acmeai.com</p>
      <div className="flex items-center gap-2 mb-4">
        {['4', '8', '2', '9', '1', '7'].map((d, i) => (
          <div key={i} className="w-10 h-12 md:w-11 md:h-12 rounded-md border border-[#1f2937] bg-[#0b1310] flex items-center justify-center text-lg font-bold text-white">
            {d}
          </div>
        ))}
      </div>
      <button className="w-full bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg">
        Verify &amp; Access Document
      </button>
    </div>
  </div>
);

// ─── Mockup 3: Document review panel (Step 03) ──────────────────────────────
const DocumentReviewMockup = () => {
  const approvals = [
    { name: 'Alex Chen', role: 'Investor', status: 'pending', date: null },
    { name: 'Sarah Kim', role: 'Founder', status: 'approved', date: 'Approved — Jun 20, 2026' },
    { name: 'Raj Patel', role: 'Legal Counsel', status: 'pending', date: null },
  ];
  return (
    <div className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl overflow-hidden" data-testid="mockup-document-review">
      <div className="px-5 py-4 border-b border-[#1f2937]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-semibold text-white truncate">SAFE Agreement — Seed Round</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Post-Money SAFE for $500,000 investment in Acme AI Inc. Governed by Delaware law.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] font-mono text-slate-400">v2</span>
            <StatusBadge tone="draft">draft</StatusBadge>
          </div>
        </div>

        {/* Reviewer Instructions */}
        <div className="mt-4 bg-amber-500/10 border-l-4 border-amber-400 rounded p-3">
          <p className="text-[10px] font-semibold text-amber-300 uppercase tracking-wide mb-1.5">
            Reviewer Instructions
          </p>
          <p className="text-xs text-amber-100 leading-relaxed">
            Please confirm the investment amount in Clause 2.1 matches the agreed $500,000. Check that the valuation cap in Clause 2.3 reflects $5M pre-money. Investor signs as Lead Investor.
          </p>
        </div>
      </div>

      {/* Approval status */}
      <div className="px-5 py-4 border-b border-[#1f2937]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-3">Approval Status</p>
        <div className="space-y-2">
          {approvals.map((a, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{a.name}</p>
                <p className="text-slate-500 text-[11px]">{a.role}</p>
              </div>
              <div className="text-right shrink-0">
                {a.status === 'approved' ? (
                  <span className="inline-flex items-center gap-1 text-[#059669] font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {a.date}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <Clock className="w-3.5 h-3.5" /> Pending
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-4 flex flex-col sm:flex-row gap-2.5">
        <button className="flex-1 bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg inline-flex items-center justify-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Approve Document
        </button>
        <button className="flex-1 border border-[#1f2937] text-slate-300 font-semibold text-sm py-2.5 rounded-lg">
          Request Changes
        </button>
      </div>
    </div>
  );
};

// ─── Mockup 4: Completion certificate (Step 04) ─────────────────────────────
// ── Sign Digitally modal mockup (step 5) ─────────────────────────────
const SignDigitallyMockup = () => (
  <div
    className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl p-5 md:p-6"
    data-testid="mockup-sign-digitally"
  >
    <div className="flex items-center gap-2.5 mb-1">
      <div className="w-8 h-8 rounded-lg bg-[#059669]/15 border border-[#059669]/40 flex items-center justify-center">
        <PenLine className="w-4 h-4 text-[#059669]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-white">Sign digitally</p>
        <p className="text-[11px] text-slate-500">Typed name rendered as your signature.</p>
      </div>
    </div>
    <div className="my-4 border-t border-[#1f2937]" />
    <div className="mb-3">
      <p className="text-[11px] text-slate-400 mb-1">Full name (editable)</p>
      <div className="bg-[#0b1310] border border-[#1f2937] rounded px-3 py-2 text-sm text-white">
        Alex Chen
      </div>
    </div>
    <div className="mb-3">
      <p className="text-[11px] text-slate-400 mb-1">Title (optional)</p>
      <div className="bg-[#0b1310] border border-[#1f2937] rounded px-3 py-2 text-sm text-white">
        Managing Partner
      </div>
    </div>
    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">
      Live preview · this is how your signature will appear
    </p>
    <div className="bg-[#0b1310] border border-[#1f2937] rounded p-4 mb-4 flex items-center">
      <span
        style={{
          fontFamily: '"Great Vibes", serif',
          fontSize: '36px',
          color: '#e2e8f0',
          lineHeight: 1,
        }}
      >
        Alex Chen
      </span>
    </div>
    <div className="flex items-start gap-2 mb-4">
      <div className="w-4 h-4 rounded border border-[#059669] bg-[#059669]/20 flex items-center justify-center mt-0.5 shrink-0">
        <CheckCircle2 className="w-3 h-3 text-[#059669]" />
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed">
        I confirm that the typed name above will serve as my electronic signature
        and has the same legal effect as a handwritten signature.
      </p>
    </div>
    <button
      type="button"
      className="w-full inline-flex items-center justify-center gap-2 bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg"
      aria-hidden="true"
    >
      <ShieldCheck className="w-4 h-4" /> Adopt &amp; Sign
    </button>
  </div>
);


// ── Execute Agreement mockup (step 6) ────────────────────────────────
const ExecuteAgreementMockup = () => (
  <div
    className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl p-5 md:p-6 space-y-4"
    data-testid="mockup-execute-agreement"
  >
    <div className="bg-blue-500/5 border border-blue-400/30 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-blue-100 inline-flex items-center gap-2">
          <PenLine className="w-3.5 h-3.5 text-blue-300" /> Sign digitally
        </p>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-400/30">
          Signed: 3 of 3
        </span>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-[#059669]">
        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>All parties have signed — anyone can now execute.</span>
      </div>
    </div>

    <div className="bg-[#059669]/10 border border-[#059669]/40 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#059669]/25 border border-[#059669] flex items-center justify-center shrink-0">
          <Stamp className="w-4 h-4 text-[#059669]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#059669]">
            All parties have signed · ready to execute
          </p>
          <p className="text-[11px] text-slate-300 mt-1 leading-relaxed">
            Stamps the document with the StartEase Agent execution seal,
            appends the signed signature page, and generates the completion
            certificate. Irreversible.
          </p>
          <button
            type="button"
            className="mt-3 inline-flex items-center gap-1.5 bg-[#059669] text-white font-semibold text-xs py-2 px-3 rounded-md"
            aria-hidden="true"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Execute Agreement
          </button>
        </div>
      </div>
    </div>

    <div className="bg-[#0b1310] border border-[#1f2937] rounded-lg p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded bg-[#059669]/15 border border-[#059669]/40 flex items-center justify-center shrink-0">
        <Download className="w-4 h-4 text-[#059669]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-white">SAFE Agreement — Seed Round (executed)</p>
        <p className="text-[10px] text-slate-500">
          Stamped PDF + signature page + completion certificate
        </p>
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#059669]/15 text-[#059669] border border-[#059669]/30 shrink-0">
        Executed
      </span>
    </div>
  </div>
);


const CompletionCertificateMockup = () => {
  const rows = [
    { name: 'Alex Chen',  role: 'Investor',       at: 'Jun 20, 2026, 14:32 UTC' },
    { name: 'Sarah Kim',  role: 'Founder',        at: 'Jun 20, 2026, 11:15 UTC' },
    { name: 'Raj Patel',  role: 'Legal Counsel',  at: 'Jun 20, 2026, 16:45 UTC' },
  ];
  return (
    <div className="bg-[#111827] border border-[#1f2937] rounded-xl shadow-2xl px-5 md:px-7 py-7" data-testid="mockup-completion-certificate">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-[#059669]/15 flex items-center justify-center">
          <CheckCircle2 className="w-9 h-9 text-[#059669]" />
        </div>
      </div>
      <h4 className="text-center text-xl md:text-2xl font-serif font-semibold text-white mb-1.5">Document Approved</h4>
      <p className="text-center text-xs md:text-sm text-slate-400 mb-5 leading-relaxed">
        All parties have reviewed and approved this document.
      </p>
      <div className="text-center text-xs text-slate-500 mb-1">
        <span className="text-slate-400">Document:</span> SAFE Agreement — Seed Round
      </div>
      <div className="text-center text-xs text-slate-500">
        <span className="text-slate-400">Company:</span> Acme AI Inc.
      </div>

      <div className="my-5 border-t border-[#1f2937]" />

      <ul className="space-y-2.5">
        {rows.map((r, i) => (
          <li key={i} className="flex items-start gap-2.5 text-xs">
            <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-white font-medium">
                {r.name} <span className="text-slate-500 font-normal">— {r.role}</span>
              </p>
              <p className="text-slate-500 text-[11px]">Approved {r.at}</p>
            </div>
          </li>
        ))}
      </ul>

      <div className="my-5 border-t border-[#1f2937]" />

      <p className="text-center text-[11px] text-slate-500 font-mono mb-3">
        Certificate ID: <span className="text-slate-300">CERT-2026-ACME-0042</span>
      </p>
      <p className="text-center text-[10px] text-slate-500 leading-relaxed mb-4 px-2">
        This certificate was generated automatically by StartEase Agent Data Room. The audit trail is permanent and tamper-proof.
      </p>
      <button className="w-full border border-[#059669]/40 text-[#059669] font-semibold text-sm py-2.5 rounded-lg hover:bg-[#059669]/5">
        Download Audit Trail PDF
      </button>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────
const InvestmentDraftingPage = () => {
  const seedDocs = [
    'SAFE Agreements',
    'Convertible Notes',
    'Angel Investment Agreements',
    'Founder & Co-Founder Agreements',
    'IP Assignment Agreements',
    'Cap Table Documentation',
    'Board Resolutions',
    'Investor Consent Documents',
  ];

  const seriesADocs = [
    'Term Sheets',
    'Share Subscription Agreements (SSA)',
    "Shareholders' Agreements (SHA)",
    'Investor Rights Agreements',
    'ESOP Documentation',
    'Side Letters',
    'Disclosure Letters',
    'Transaction Closing Documents',
  ];

  const txSupport = [
    'Due Diligence Reports',
    'Investment Structuring Notes',
    'Corporate Authorizations',
    'Closing Checklists',
    'Regulatory Filings',
  ];

  return (
    <div className={`${BG} min-h-screen text-white`} data-testid="investment-drafting-page">
      <SEOHead
        title="Investment Document Drafting for AI Startups — Seed & Series A | StartEase Agent"
        description="We draft SAFEs, SSAs, SHAs, term sheets and closing documents for AI companies raising Seed and Series A rounds. Reviewed in a secure Data Room. Full audit trail."
        canonical="https://starteaseai.com/investment-drafting"
      />

      <Header />

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section className="relative pt-20 pb-24 md:pt-28 md:pb-32" data-testid="hero-section">
        <div className="container-custom max-w-5xl">
          <div
            className="inline-flex items-center gap-2 bg-[#059669]/10 border border-[#059669]/30 rounded-full px-4 py-1.5 mb-7"
            data-testid="hero-badge"
          >
            <FileText className="w-3.5 h-3.5 text-[#059669]" />
            <SectionLabel>StartEase Document Services</SectionLabel>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-6">
            <span className="block text-white">Your AI startup is raising.</span>
            <span className="block text-slate-400 mt-1">
              One bad clause will cost you more than the round.
            </span>
          </h1>

          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mb-5">
            SAFEs with missing carve-outs. Founder agreements without IP assignment.
            ESOP pools that trigger a top-up demand at Series A. Investor rights
            clauses that touch your data.
          </p>

          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl mb-10">
            We draft investment documents for AI companies at Seed and Series A.
            Every document written for your deal. Reviewed in a secure Data Room.
            Approved with a full audit trail.
          </p>

          <div className="flex flex-col items-start gap-3">
            <PrimaryCTA testId="hero-cta">Consultation Call</PrimaryCTA>
            <p className="text-xs text-slate-500 max-w-xl leading-relaxed" data-testid="hero-note-1">
              No StartEase formation order required. If your company is a US LLC
              or C-Corp — formed anywhere — we can help.
            </p>
            <p className="text-xs text-slate-500 max-w-xl leading-relaxed" data-testid="hero-note-2">
              We work primarily with AI companies. Raising a non-AI round? Book
              a call — we&apos;ll tell you if we can help.
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHY AI DEALS ARE DIFFERENT ────────────────────────────── */}
      <section className={`${BG_PANEL} py-20 md:py-24 border-y ${BORDER}`} data-testid="why-section">
        <div className="container-custom max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>Why AI deals are different</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
              Most investment document specialists have never worked on an AI deal.
            </h2>
            <p className="text-lg text-slate-400">They miss things.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ReasonCard
              testId="why-card-ip"
              title="IP Ownership"
              body="Who owns the model? The training data? The fine-tuned weights? If your founders agreement doesn't answer this, your Series A investor will."
            />
            <ReasonCard
              testId="why-card-vesting"
              title="Founder Vesting"
              body="Standard 4-year cliff vesting doesn't account for AI co-founder dynamics. One founder leaves early. Who owns the model they trained?"
            />
            <ReasonCard
              testId="why-card-esop"
              title="ESOP Pool Sizing"
              body="AI companies hire ML engineers fast. An under-sized option pool at Seed triggers a top-up demand before your Series A closes."
            />
            <ReasonCard
              testId="why-card-data"
              title="Data Rights"
              body="Some investor rights clauses create data access obligations. Most founders don't catch this until it becomes a problem."
            />
          </div>

          <p className="mt-10 text-lg md:text-xl font-semibold text-white" data-testid="why-end-line">
            We have. That&apos;s the difference.
          </p>
        </div>
      </section>

      {/* ─── WHAT WE DRAFT ─────────────────────────────────────────── */}
      <section className="py-20 md:py-24" data-testid="what-we-draft-section">
        <div className="container-custom max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>What we draft</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight mt-4">
              Documents for every stage of your raise.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {/* Seed Round */}
            <div className={`${BG_PANEL} ${BORDER} border rounded-xl p-7`} data-testid="seed-col">
              <h3 className="text-xl font-semibold text-[#059669] mb-5 flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-[#059669]/15 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </span>
                Seed Round
              </h3>
              <ul className="space-y-0.5">
                {seedDocs.map((d) => (
                  <DocBullet key={d}>{d}</DocBullet>
                ))}
              </ul>
            </div>

            {/* Series A */}
            <div className={`${BG_PANEL} ${BORDER} border rounded-xl p-7`} data-testid="series-a-col">
              <h3 className="text-xl font-semibold text-[#059669] mb-5 flex items-center gap-2">
                <span className="w-7 h-7 rounded-md bg-[#059669]/15 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </span>
                Series A
              </h3>
              <ul className="space-y-0.5">
                {seriesADocs.map((d) => (
                  <DocBullet key={d}>{d}</DocBullet>
                ))}
              </ul>
            </div>
          </div>

          {/* Transaction Support */}
          <div className={`${BG_PANEL} ${BORDER} border rounded-xl p-7`} data-testid="tx-support-col">
            <h3 className="text-xl font-semibold text-[#059669] mb-5 flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-[#059669]/15 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4" />
              </span>
              Transaction Support
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0">
              {txSupport.map((d) => (
                <DocBullet key={d}>{d}</DocBullet>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ──────────────────────────────────────────── */}
      <section className={`${BG_PANEL} py-20 md:py-24 border-y ${BORDER}`} data-testid="how-it-works-section">
        <div className="container-custom max-w-5xl">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>How it works</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight mt-4">
              Four steps. One clean document. Nothing on WhatsApp.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
            <Step
              testId="step-1"
              n="1"
              title="Book a call"
              body="30 minutes. You walk us through your deal — stage, structure, investors, what's already agreed. We ask the right questions."
            />
            <Step
              testId="step-2"
              n="2"
              title="We draft"
              body="Every document written for your specific deal. Your cap table. Your investor's requirements. Your state's governing law. Not a template."
            />
            <Step
              testId="step-3"
              n="3"
              title="Review in your Data Room"
              body="Draft delivered into a private, secure Data Room. Founders, investors, and counsel log in with OTP. No account needed. Review, comment, request changes. Everything tracked."
            />
            <Step
              testId="step-4"
              n="4"
              title="Approve and lock"
              body="All parties approve. Completion certificate generated. Full audit trail locked permanently. Document saved to your vault."
            />
          </div>
        </div>
      </section>

      {/* ─── DATA ROOM FEATURE ─────────────────────────────────────── */}
      <section className="py-20 md:py-24" data-testid="data-room-feature-section">
        <div className="container-custom max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <SectionLabel>Data Room — included with every drafting order.</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-5">
              Every approval locked. Permanently.
            </h2>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed">
              When your document is ready, it goes into a private, secure Data
              Room. Your investors, co-founders, and counsel log in with a
              6-digit OTP — no account needed. They review, comment, and approve.
              Every response is locked with a timestamp the moment it&apos;s
              submitted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ReasonCard
              testId="data-room-feature-card-1"
              title="OTP Login — No Passwords"
              body="Investors receive one email. They enter their OTP and they're in. No account creation, no password to remember."
            />
            <ReasonCard
              testId="data-room-feature-card-2"
              title="Version Control"
              body="Every draft preserved. When you upload a revised version, all parties are notified and re-approval begins. Old versions never deleted."
            />
            <ReasonCard
              testId="data-room-feature-card-3"
              title="Completion Certificate"
              body="When all parties approve, a branded certificate is generated automatically and sent to everyone. Legally defensible record."
            />
            <ReasonCard
              testId="data-room-feature-card-4"
              title="Full Audit Trail"
              body="Every action logged permanently — who viewed, who commented, who approved, and when. Export as branded PDF at any time."
            />
          </div>

          <p
            className="mt-12 text-lg md:text-xl font-semibold text-white text-center"
            data-testid="data-room-feature-end-line"
          >
            No WhatsApp. No email chains. No &lsquo;I never saw that version.&rsquo;
          </p>
          <p className="mt-4 text-center">
            <a
              href="/login"
              className="text-sm text-slate-500 hover:text-[#059669] transition-colors inline-flex items-center gap-1"
              data-testid="data-room-feature-cta-link"
            >
              Already a StartEase client? Access your Data Room <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </p>
        </div>
      </section>

      {/* ─── SEE IT IN ACTION (step-by-step mockups) ───────────────── */}
      <section
        className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-28`}
        data-testid="see-it-in-action-section"
      >
        <div className="container-custom max-w-6xl">
          <div className="mb-16 max-w-3xl">
            <SectionLabel>See it in action</SectionLabel>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
              From document to executed agreement — in six steps.
            </h2>
            <p className="text-base md:text-lg text-slate-400 leading-relaxed">
              Here&apos;s exactly what happens after your document is drafted.
            </p>
          </div>

          <div className="space-y-20 md:space-y-28">
            <StepBlock
              testId="step-01-row"
              number="01"
              title="Your document lands in a secure Data Room."
              body="Once your document is drafted and reviewed internally, we create a private Data Room for your deal. The document is uploaded with version tracking enabled. Every future revision is preserved — nothing is ever deleted."
              mockup={<DocumentListMockup />}
              mockupOnRightDesktop
            />

            <StepBlock
              testId="step-02-row"
              number="02"
              title="Investors get one email. That's it."
              body="No account creation. No password. Your investor receives a single email with a secure link. They enter a 6-digit OTP and they're inside the document — ready to review."
              mockup={<EmailInviteAndOtpMockup />}
              mockupOnRightDesktop={false}
            />

            <StepBlock
              testId="step-03-row"
              number="03"
              title="Your investor sees the document. Nothing else."
              body="Inside the Data Room, your investor sees the document, your reviewer instructions, and one decision — Approve or Request Changes. Every action is logged with a timestamp the moment it happens."
              mockup={<DocumentReviewMockup />}
              mockupOnRightDesktop
            />

            <StepBlock
              testId="step-04-row"
              number="04"
              title="Everyone approves. Document is ready to sign."
              body="When the last party hits Approve, the document unlocks the signing stage. Every action so far — uploads, comments, approvals, timestamps — is already logged. Nothing can be backdated."
              mockup={<DocumentReviewMockup />}
              mockupOnRightDesktop={false}
            />

            <StepBlock
              testId="step-05-row"
              number="05"
              title="Each party signs digitally. One click."
              body="Every signer pre-fills their name, sees their signature rendered in cursive in a live preview, ticks the consent box, and clicks Adopt & Sign. No drawing, no image uploads, no separate signing service — just a typed name with a legal e-signature record (timestamp + IP captured server-side)."
              mockup={<SignDigitallyMockup />}
              mockupOnRightDesktop
            />

            <StepBlock
              testId="step-06-row"
              number="06"
              title="Execute the agreement. Certificate locked forever."
              body="Once everyone has signed, anyone can execute. One click stamps the document with the StartEase execution seal, appends the signed signature page, generates a tamper-proof completion certificate, and locks the audit trail permanently. The executed PDF and the certificate are downloadable by every party — instantly."
              mockup={<ExecuteAgreementMockup />}
              mockupOnRightDesktop={false}
            />
          </div>

          <div className="mt-20 md:mt-24 text-center">
            <p
              className="font-serif text-2xl md:text-3xl font-semibold text-white tracking-tight mb-7"
              data-testid="see-it-end-line"
            >
              Your deal. Documented. Approved. Locked.
            </p>
            <div className="flex justify-center">
              <PrimaryCTA testId="see-it-cta">Consultation Call</PrimaryCTA>
            </div>
          </div>
        </div>
      </section>

      {/* ─── THE SPECIALIST ────────────────────────────────────────── */}
      <section className="py-20 md:py-24" data-testid="specialist-section">
        <div className="container-custom max-w-4xl">
          <SectionLabel>The drafting specialist</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-7">
            An experienced specialist. Not an AI. Not a template engine.
          </h2>

          <div className="space-y-5">
            <p className="text-base md:text-lg text-slate-300 leading-relaxed">
              Our lead drafting professional specialises in investment documentation
              across Seed and Series A transactions — term sheets, SAFEs, SSAs,
              SHAs, fund documents, and full closing packages for AI companies.
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              We are not a licensed US law firm and do not provide legal advice.
              If your transaction needs a licensed US attorney, we will tell you
              before you book.
            </p>
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ─────────────────────────────────────────────── */}
      <section
        className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-24`}
        data-testid="final-cta-section"
      >
        <div className="container-custom max-w-3xl text-center">
          <div
            className="inline-flex items-center gap-2 bg-[#059669]/10 border border-[#059669]/30 rounded-full px-4 py-1.5 mb-6"
            aria-hidden="true"
          >
            <Calendar className="w-3.5 h-3.5 text-[#059669]" />
            <SectionLabel>Ready when you are</SectionLabel>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight mb-5">
            Closing a round? Let&apos;s talk.
          </h2>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
            Book a 30-minute call. Tell us about your deal. We&apos;ll confirm
            what you need and whether we can help. No invoice until you approve
            the scope.
          </p>
          <div className="flex justify-center">
            <PrimaryCTA testId="final-cta">Consultation Call</PrimaryCTA>
          </div>
        </div>
      </section>

      {/* ─── LEGAL DISCLAIMER ──────────────────────────────────────── */}
      <section className={`${BG} py-10`} data-testid="legal-disclaimer-section">
        <div className="container-custom max-w-4xl">
          <p className="text-xs text-slate-500 leading-relaxed text-center">
            StartEase Agent drafting professionals are experienced investment
            document drafting specialists. They are not licensed attorneys or
            advocates and do not provide legal advice.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InvestmentDraftingPage;
